<template>
  <div
    v-if="
      field.fieldObj &&
      field.fieldObj.field_type &&
      !isFieldHidden &&
      !(field.hide_on_mobile && Helper.isMobileSize())
    "
  >
    <!-- INPUT TEXT -->
    <InputText
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.INPUT_TXT"
      :id="field.id"
      :inputId="field.id"
      class="w-full"
      :invalid="field.invalid"
      v-model="field.value"
      :disabled="field.disabled"
      :placeholder="field.placeholder || ''"
      v-tooltip="field.placeholder || ''"
      fluid
      @input="($event) => onUpdateField($event.target.value)"
    />
    <!-- CALCULATED -->
    <IconField
      class="flex items-center"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.CALCULATED"
    >
      <InputIcon
        v-if="field.prefixIcon"
        :class="`pi ${field.prefixIcon}`"
      />
      <InputText
        :disabled="true"
        :id="field.id"
        :inputId="field.id"
        class="w-full"
        :invalid="field.invalid"
        :value="field.value"
        :placeholder="field.placeholder || ''"
        v-tooltip="field.placeholder || ''"
        fluid
        @update:modelValue="onUpdateField"
      />
    </IconField>
    <!-- REF_FIELD -->
    <IconField
      class="flex items-center"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.REF_FIELD"
    >
      <InputIcon
        v-if="field.prefixIcon"
        :class="`pi ${field.prefixIcon}`"
      />
      <InputText
        :disabled="true"
        :id="field.id"
        :inputId="field.id"
        class="w-full"
        :invalid="field.invalid"
        :value="field.value"
        :placeholder="field.placeholder || ''"
        v-tooltip="field.placeholder || ''"
        fluid
        @update:modelValue="onUpdateField"
      />
    </IconField>
    <!-- AUTO_INCREMENT -->
    <IconField
      class="flex items-center"
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.AUTO_INCREMENT
      "
    >
      <InputIcon
        v-if="field.prefixIcon"
        :class="`pi ${field.prefixIcon}`"
      />
      <InputText
        :disabled="true"
        :id="field.id"
        :inputId="field.id"
        class="w-full"
        :invalid="field.invalid"
        :value="field.value"
        :placeholder="field.placeholder || ''"
        v-tooltip="field.placeholder || ''"
        fluid
        @update:modelValue="onUpdateField"
      />
    </IconField>
    <!-- INPUT NUMBER -->
    <InputNumber
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.INPUT_NUMBER
      "
      v-model="field.value"
      :inputId="field.id"
      :invalid="field.invalid"
      :disabled="field.disabled"
      :minFractionDigits="field.minFractionDigits ?? 2"
      :maxFractionDigits="field.maxFractionDigits ?? 2"
      :min="field.min"
      :max="field.max"
      fluid
      class="w-full"
      :prefix="field.prefix || ``"
      showButtons
      :placeholder="field.placeholder || ''"
      v-tooltip="field.placeholder || ''"
      @input="($event) => onUpdateField($event.value)"
      @update:modelValue="onUpdateField"
    />
    <!-- DATE PICKER -->
    <DatePicker
      v-if="
        field.fieldObj.field_type ==
        Constants.enums.FIELD_TYPES.INPUT_DATE_TIME_PICKER
      "
      :id="field.id"
      :inputId="field.id"
      :disabled="field.disabled"
      class="w-full"
      v-model="field.value"
      @update:modelValue="onUpdateField"
      :showTime="field.showTime || false"
      :timeOnly="field.timeOnly || false"
      :showSeconds="field.showSeconds || false"
      :selectionMode="field.mode || 'single'"
      :hourFormat="field.hourFormat || '12'"
      :view="field.view || 'date'"
      showIcon
      :dateFormat="field.dateFormat || `dd/mm/yy`"
      :placeholder="field.placeholder || ''"
      v-tooltip="field.placeholder || ''"
      :invalid="field.invalid"
      fluid
    />
    <!-- TEXT AREA -->
    <div class="relative w-full">
      <Textarea
        v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TXT_AREA"
        :id="field.id"
        class="w-full"
        :disabled="field.disabled"
        :rows="field.rows || 3"
        :cols="field.cols || 50"
        :autoResize="field.autoResize || false"
        v-model="field.value"
        :maxlength="field.max_length"
        :placeholder="field.placeholder || ''"
        v-tooltip="field.placeholder || ''"
        :invalid="field.invalid"
        @update:modelValue="onUpdateField"
      />
      <small
        v-if="field.char_count_display"
        class="absolute right-2 bottom-3 text-gray-500 text-xs"
      >
        {{ field.value?.length || 0 }}/{{ field.max_length }}
      </small>
    </div>
    <!-- SELECT -->
    <Select
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.DROPDOWN"
      :disabled="field.disabled"
      :id="field.id"
      filter
      :invalid="field.invalid"
      :showClear="
        !(
          Helper.isValueExist(field.on_change_remove) ||
          Helper.isValueExist(field.showContainMatch)
        )
      "
      class="w-full"
      v-model="field.value"
      :options="getMenuOptions()"
      :optionLabel="field.optionLabel || 'label'"
      :optionValue="field.optionValue || null"
      :options-ref="field.options_ref"
      :placeholder="field.placeholder || ''"
      @update:modelValue="onUpdateField"
    >
      <template #option="slotProps">
        <div class="flex items-center w-full">
          <div class="flex-none">
            {{ slotProps.option[field.optionLabel] || slotProps.option.label }}
          </div>
          <div class="flex-grow"></div>
          <div
            class="flex-none"
            v-if="
              Helper.isValueExist(field.options_ref) &&
              Helper.isValueExist(field.add_options_form_template)
            "
          >
            <Button
              icon="pi pi-pencil"
              aria-label="Edit"
              variant="text"
              rounded
              severity="secondary"
              @click.stop="onMenuOptionEdit(slotProps.option)"
            />
            <Button
              icon="pi pi-times"
              aria-label="Delete"
              variant="text"
              rounded
              severity="danger"
              @click.stop="onMenuOptionDelete(slotProps.option)"
            />
          </div>
        </div>
      </template>
      <template #footer>
        <div
          class="p-3"
          v-if="
            Helper.isValueExist(field.options_ref) &&
            Helper.isValueExist(field.add_options_form_template)
          "
        >
          <Button
            label="Add New"
            fluid
            severity="secondary"
            text
            icon="pi pi-plus"
            @click="onAddNewMenuOption"
          />
        </div>
      </template>
    </Select>
    <!-- TOGGLE -->
    <div
      class="flex items-center gap-2"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TOGGLE"
    >
      <!-- prefix -->
      <ToggleSwitch
        v-if="field.prefix === 'toggle'"
        :disabled="field.disabled"
        :id="field.id"
        :inputId="field.id"
        v-model="field.value"
        :invalid="field.invalid"
        @update:modelValue="onUpdateField"
      />
      <span class="flex-1">{{ field.label || "" }}</span>

      <!-- suffix -->
      <ToggleSwitch
        v-if="!field.prefix && field.suffix === 'toggle'"
        :disabled="field.disabled"
        :id="field.id"
        :inputId="field.id"
        v-model="field.value"
        :invalid="field.invalid"
        @update:modelValue="onUpdateField"
      />
    </div>
    <!-- CHECKBOX -->
    <div
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.CHECKBOX"
    >
      <div
        v-if="field.style_variant === 'button'"
        class="grid gap-2 w-full"
        :class="{
          'grid-cols-1': !field.max_item_in_mobile_view,
          [`grid-cols-${field.max_item_in_mobile_view}`]:
            field.max_item_in_mobile_view,
          'sm:flex sm:flex-row': true
        }"
      >
        <button
          type="button"
          @click="
            () => {
              field.value = !field.value;
              onUpdateField(field.value);
            }
          "
          :disabled="field.disabled"
          :class="[
            'flex-1 text-left px-6 py-2 rounded-lg font-medium transition border',
            field.value
              ? 'bg-black text-white border-black'
              : 'bg-slate-100 text-gray-700 border-transparent',
            field.invalid ? 'ring-1 ring-red-400' : ''
          ]"
        >
          <span v-html="field.label"></span>
        </button>
      </div>
      <div
        v-else
        class="flex gap-2 items-center"
      >
        <!-- checkbox with left(default) -->
        <template v-if="field.checkbox_position !== 'right'">
          <Checkbox
            :disabled="field.disabled"
            :id="field.id"
            :inputId="`${field.id}`"
            :name="field.label"
            :binary="true"
            class="w-full"
            v-model="field.value"
            v-tooltip="field.placeholder || ''"
            :invalid="field.invalid"
            @update:modelValue="onUpdateField"
          />
          <label
            :for="field.id"
            v-if="field.label"
          >
            <span v-html="field.label"></span>
          </label>
        </template>

        <!-- checkbox with right -->
        <template v-else>
          <label
            :for="field.id"
            v-if="field.label"
          >
            <span v-html="field.label"></span>
          </label>
          <Checkbox
            :disabled="field.disabled"
            :id="field.id"
            :inputId="`${field.id}`"
            :name="field.label"
            :binary="true"
            class="w-full"
            v-model="field.value"
            v-tooltip="field.placeholder || ''"
            :invalid="field.invalid"
            @update:modelValue="onUpdateField"
          />
        </template>
      </div>
    </div>
    <!-- RADIO GROUP - default or button style -->
    <div
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.RADIO_GROUP
      "
    >
      <div
        v-if="field.style_variant === 'button'"
        class="grid gap-2 w-full"
        :class="{
          'grid-cols-1': !field.max_item_in_mobile_view,
          [`grid-cols-${field.max_item_in_mobile_view}`]:
            field.max_item_in_mobile_view,
          'sm:flex sm:flex-row': true
        }"
      >
        <button
          v-for="item in field.options"
          :key="item.value"
          type="button"
          @click="
            () => {
              field.value = item.value;
              onUpdateField(item.value);
              // now see if we need to set any extra values as well
              if (field.set_value_to) {
                for (const _field of field.set_value_to) {
                  console.log(`UPDATING ${_field} to ${item.value}`);
                  onUpdateField(item.value, _field);
                }
              }
            }
          "
          :class="[
            'flex-1 text-center px-6 py-2 rounded-lg font-medium transition border',
            field.value === item.value
              ? 'bg-black text-white border-black'
              : 'bg-slate-100 text-gray-700 border-transparent',
            field.invalid ? 'ring-1 ring-red-400' : ''
          ]"
        >
          {{ item.label }}
        </button>
      </div>

      <!-- fallback: default radio button -->
      <div
        v-else
        class="flex items-center gap-4"
      >
        <div
          v-for="item in field.options"
          :key="item.label"
          class="flex items-center gap-2"
        >
          <RadioButton
            :disabled="field.disabled"
            v-model="field.value"
            :inputId="item.label"
            :name="field.fieldObj.field_name"
            :invalid="field.invalid"
            :value="item.value"
            @update:modelValue="onUpdateField"
          />
          <label :for="item.label">
            <span v-html="item.label"></span>
          </label>
        </div>
      </div>
    </div>

    <!-- HIDDEN FIELD -->
    <div
      class=""
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.HIDDEN_FIELD
      "
    >
      <div>{{ getHiddenFieldContent() }}</div>
    </div>
    <!-- HTML -->
    <div
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.HTML"
      :options-ref="field.options_ref"
      class="relative"
      :class="{
        'bg-surface-100 p-3 rounded': htmlIsEditable
      }"
    >
      <div class="absolute top-0 right-0 z-10">
        <Button
          v-if="htmlIsEditable"
          icon="pi pi-pencil"
          aria-label="Edit"
          variant="text"
          :disabled="field.disabled"
          rounded
          severity="info"
          v-tooltip="field.tooltip || field.placeholder || `Edit`"
          @click="onHtmlEdit(field)"
        />
        <Button
          v-if="htmlIsRemovable"
          icon="pi pi-trash"
          aria-label="Remove"
          variant="text"
          :disabled="field.disabled"
          rounded
          severity="danger"
          v-tooltip="field.tooltip || field.placeholder || `Remove`"
          @click="onHtmlRemove()"
        />
      </div>
      <div
        v-html="field.label ? processHtmlContent(field.label) : ''"
        :class="{
          'inline-block align-top': field.required
        }"
      ></div>
      <span
        class="required"
        v-if="field.required"
      >
        *
      </span>
    </div>
    <!-- TITLE 1 -->
    <div
      class="text-lg sm:text-xl font-bold"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TITLE_1"
    >
      <span v-html="field.label || field.placeholder || ''"></span>
      <span
        class="required"
        v-if="field.required"
      >
        *
      </span>
    </div>
    <!-- TITLE 2 -->
    <div
      class="text-md sm:text-lg font-semibold"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TITLE_2"
    >
      <span v-html="field.label || field.placeholder || ''"></span>
      <span
        class="required"
        v-if="field.required"
      >
        *
      </span>
    </div>
    <!-- TITLE 3 -->
    <div
      class="text-md sm:text-lg"
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TITLE_3"
    >
      <span v-html="field.label || field.placeholder || ''"></span>
      <span
        class="required"
        v-if="field.required"
      >
        *
      </span>
    </div>
    <!-- TEXT -->
    <div
      v-if="field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TXT"
      :class="`pl-${(field.indent_level || 0) * 4}`"
    >
      <span v-html="field.label || field.placeholder || ''"></span>
      <span
        class="required"
        v-if="field.required"
      >
        *
      </span>
    </div>
    <!-- SINGLE SIGNATURE / MOVE TO COMPONENT -->
    <div
      v-if="
        field.fieldObj.field_type ==
        Constants.enums.FIELD_TYPES.SINGLE_SIGNATURE
      "
    >
      <div
        class="mt-2"
        style="height: 150px"
        v-if="field.value"
      >
        <div class="w-full border rounded p-2 relative shadow-xs">
          <div class="flex items-center justify-between pb-2">
            <div class="flex items-center">
              <p class="pl-3">
                <span class="font-semibold text-gray-800">
                  {{ field.value.attendeeName }}
                  <span
                    v-if="field.value.attendeeType"
                    class="text-sm font-semibold text-gray-500 ml-2"
                  >
                    {{ field.value.attendeeType }}
                  </span>
                  <span
                    v-if="field.value.signatureDate"
                    class="text-sm font-semibold text-gray-500 ml-2"
                  >
                    {{
                      `(${Helper.convertToLocalDateTime(field.value.signatureDate)})`
                    }}
                  </span>
                </span>
              </p>
            </div>
            <!-- Remove Button -->
            <Button
              icon="pi pi-times"
              class="p-button-text p-button-sm text-red-500"
              :disabled="field.disabled"
              @click="
                () => {
                  if (!field.disabled) {
                    field.value = null;
                  }
                }
              "
              aria-label="Remove"
            />
          </div>
          <img
            :src="field.value.signatureData"
            class="object-contain max-h-[100px] w-full"
          />
        </div>
      </div>
      <div
        v-else
        class="flex flex-col mt-2"
        style="height: 150px"
      >
        <div
          class="border-2 border-dashed rounded flex-auto flex justify-center items-center font-medium cursor-pointer"
          :style="{
            borderColor: field.invalid ? '#ef4444' : '#e5e7eb',
            backgroundColor: field.invalid ? '#fef2f2' : '#f9fafb'
          }"
          @click="
            () => {
              if (!field.disabled) {
                signatureObj.isVisible = true;
              }
            }
          "
        >
          <i class="pi pi-plus text-2xl mr-2"></i>
          {{ field.placeholder || `Add Signature` }}
        </div>
      </div>
    </div>
    <!-- ATTENDEE SIGNATURES -->
    <div
      v-if="
        field.fieldObj.field_type ==
        Constants.enums.FIELD_TYPES.ATTENDEE_SIGNATURES
      "
    >
      <AttendeesSignatureField
        :attendees="attendeeSignaturesData"
        :disabled="field.disabled"
        :invalid="field.invalid"
        :response="response"
        :attendeeDataKey="field.attendeeDataKey"
        :updateStore="updateStore"
      />
    </div>
    <!-- SIMPLE NAME INPUT -->
    <div
      v-if="
        field.fieldObj.field_type ==
        Constants.enums.FIELD_TYPES.SIMPLE_NAME_INPUT
      "
    >
      <AutoComplete
        ref="autoCompleteRef"
        :id="field.id"
        class="w-full"
        :invalid="field.invalid"
        v-model="field.value"
        :disabled="field.disabled"
        :placeholder="field.placeholder || 'Search Name or Enter Name'"
        :suggestions="autoCompleteSuggestions"
        optionLabel="label"
        @complete="onAutoCompleteSearch"
        @item-select="(event) => onUpdateField(event.value)"
        @click="onAutoCompleteClick"
        fluid
      />
    </div>
    <!-- TRIGGER BUTTON -->
    <Button
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.TRIGGER_BTN
      "
      :label="field.label || ''"
      :severity="field.severity"
      :disabled="field.disabled"
      :icon="field.icon || ''"
      @click="onBtnClick(field.onClick)"
      :badge="field.value && field.value.length ? `${field.value.length}` : ''"
      badgeSeverity="contrast"
      v-tooltip.top="
        field.disabled && field.disabled_tooltip ? field.disabled_tooltip : ''
      "
    />
    <!-- STATUS BAR -->
    <Tag
      v-if="
        field.fieldObj.field_type == Constants.enums.FIELD_TYPES.STATUS_BAR &&
        field.value
      "
      :class="field.onClick ? `cursor-pointer` : ``"
      class="w-full mb-3"
      :icon="
        field.color_map.find((item) => item.status === (field.value || ''))
          ?.icon || ''
      "
      :severity="
        field.color_map.find((item) => item.status === (field.value || ''))
          ?.severity || 'info'
      "
      :value="field.value"
      @click="field.onClick ? onBtnClick(field.onClick) : null"
    />
  </div>
  <!-- SNAPSHOT VIEW DIALOG -->
  <SnapShotViewDialog
    v-if="docObj.isVisible"
    :visible="docObj.isVisible"
    :url="docObj.url"
    :name="docObj.fileName"
    :type="docObj.media_type"
    @update:visible="docObj.isVisible = $event"
  />
  <!-- CAMERA INPUT -->
  <CameraInput
    v-if="isCameraInputRequired"
    ref="cameraInput"
    captureType="environment"
    :acceptedType="field.acceptedType || ''"
    @file-selected="handleFileSelection"
  />
  <!-- MEDIA SOURCE DIALOG -->
  <MediaSourceDialog
    v-if="mediaSourceDialog.isVisible"
    :visible="mediaSourceDialog.isVisible"
    header="Select Source"
    @update:visible="mediaSourceDialog.isVisible = $event"
    @select="onMediaSourceSelect"
  />
  <!-- ERROR DIALOG -->
  <ErrorDialog
    v-if="errorObj.isVisible"
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
  <!-- FULL SCREEN OVERLAY LOADER -->
  <FullScreenOverlayLoader
    v-if="loaderObj.isLoading"
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />
  <!-- SIGNATURE DIALOG -->
  <SignatureDialog
    v-if="signatureObj.isVisible"
    v-model:visible="signatureObj.isVisible"
    :skipAttendee="field.skipAttendee"
    @onConfirm="signatureObj.onConfirm"
  />
  <!-- Add More Menu Option Dialog -->
  <AddMoreMenuOptionsComponent
    v-if="
      field.fieldObj &&
      field.fieldObj.field_type &&
      (field.fieldObj.field_type === Constants.enums.FIELD_TYPES.DROPDOWN ||
        field.fieldObj.field_type === Constants.enums.FIELD_TYPES.HTML ||
        (field.fieldObj.field_type ===
          Constants.enums.FIELD_TYPES.TRIGGER_BTN &&
          Helper.isValueExist(field.add_options_form_template))) &&
      addMoreMenuObj.isVisible
    "
    :pageObj="addMoreMenuObj.pageObj"
    :title="addMoreMenuObj.title"
    :visible="addMoreMenuObj.isVisible"
    :templateData="formTemplateData"
    :onCancelClick="addMoreMenuObj.onCancelClick || null"
    @update:visible="addMoreMenuObj.isVisible = $event"
    :onSaveClick="addMoreMenuObj.onSaveClick"
  />
  <ConfirmationDialog
    v-if="confirmDialogObj.isVisible"
    :visible="confirmDialogObj.isVisible"
    :headerMsg="confirmDialogObj.headerMsg"
    :message="confirmDialogObj.message"
    :showHazardIcon="confirmDialogObj.showHazardIcon"
    :onConfirm="confirmDialogObj.onConfirm"
    @update:visible="confirmDialogObj.isVisible = $event"
  />
  <imageEditDialog
    v-if="newImageObj.isVisible"
    :visible="newImageObj.isVisible"
    :url="newImageObj.url"
    :name="newImageObj.name"
    :type="newImageObj.type"
    :originalFile="newImageObj.originalFile"
    @update:visible="handleImageEditDialogVisibility"
    @save="processFiles"
  />
  <!-- Google Maps Dialog -->
  <GoogleMapsDialog
    v-if="googleMapsDialog.isVisible"
    v-model:visible="googleMapsDialog.isVisible"
    :latitude="googleMapsDialog.location?.latitude"
    :longitude="googleMapsDialog.location?.longitude"
    :clickable="googleMapsDialog.clickable"
    title="Select Location"
    @coordinates-updated="handleCoordinatesUpdated"
  />
  <!-- Information Dialog -->
  <InformationDialog
    v-model="informationDialog.isVisible"
    :header="informationDialog.header"
    :message="informationDialog.message"
    :showIcon="informationDialog.showIcon"
  />
</template>

<script>
  import {
    defineComponent,
    onMounted,
    ref,
    watch,
    computed,
    onUnmounted,
    nextTick
  } from "vue";
  import _ from "lodash";
  import { storeToRefs } from "pinia";
  import Helper from "@/utils/helper";
  import Constants from "@/utils/constants";
  import SnapShotViewDialog from "@/components/SnapShotViewDialog.vue";
  import CameraInput from "@/components/CameraInput.vue";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";
  import SignatureDialog from "@/components/SignatureDialog.vue";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import AddMoreMenuOptionsComponent from "@/components/AddMoreMenuOptionsComponent.vue";
  import AttendeesSignatureField from "@/components/AttendeesSignatureField.vue";
  import helper from "../utils/helper";
  import { usePreDefinedListStore } from "../stores/preDefinedListStore";
  import { useFormTemplateResponseStore } from "../stores/formTemplateResponseStore";
  import { useFormPageStore } from "../stores/formPageStore";
  import { useSupabaseStorageUploadStore } from "@/stores/supabaseStorageUploadStore";
  import { useAttachmentsStore } from "@/stores/attachmentsStore";
  import { useFormTemplateStore } from "@/stores/formTemplateStore";
  import { usePermissionsStore } from "@/stores/permissionsStore";
  import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
  import imageEditDialog from "@/components/ImageEditDialog.vue";
  import GoogleMapsDialog from "@/components/GoogleMapsDialog.vue";
  import InformationDialog from "@/components/InformationDialog.vue";
  import MediaSourceDialog from "@/components/MediaSourceDialog.vue";
  import { useRouter, useRoute } from "vue-router";

  export default defineComponent({
    name: "FormInputComponent",
    components: {
      SnapShotViewDialog,
      CameraInput,
      FullScreenOverlayLoader,
      SignatureDialog,
      ErrorDialog,
      AddMoreMenuOptionsComponent,
      AttendeesSignatureField,
      ConfirmationDialog,
      imageEditDialog,
      GoogleMapsDialog,
      InformationDialog,
      MediaSourceDialog
    },
    props: {
      field: {
        type: Object
      },
      isMultiple: {
        type: Boolean,
        default: false
      },
      isSubPage: {
        type: Boolean,
        default: false
      },
      multipleParentId: {
        type: [String, Number, Object],
        default: ""
      },
      recordIndex: {
        type: Number,
        default: -1
      },
      rowGroup: {
        type: Object,
        default: () => ({})
      },
      updateLocalStore: {
        type: Boolean,
        default: true
      },
      clearOnHidden: {
        type: Boolean,
        default: false
      },
      isVisible: {
        type: Boolean,
        default: true
      },
      selectedPath: {
        type: Array,
        default: () => []
      },
      ignoreOverridePageValue: {
        type: Boolean,
        default: false
      }
    },
    setup(props) {
      const autoCompleteSuggestions = ref([]);
      const autoCompleteRef = ref(null);
      const router = useRouter();
      const route = useRoute();
      const formTemplateResponseStore = useFormTemplateResponseStore();
      let { response, templateId } = storeToRefs(formTemplateResponseStore);
      // const field = ref(Helper.deepCopy(props.field));
      const field = ref(props.field);

      const isFieldHidden = computed(() => {
        return Helper.isFieldHidden(field.value, response.value);
      });

      // pull data from the response object using the selected path
      const _contextData =
        props.selectedPath && props.selectedPath.length > 0
          ? Helper.findAndSetKeyInObject(
              response.value,
              props.selectedPath[props.selectedPath.length - 1]
            )
          : Helper.deepCopy(response.value);

      // using context data we will save the value that should be displayed in the field
      if (props.isMultiple && Helper.isValueExist(props.recordIndex)) {
        // For multiple fields, get data from the specific record index
        const parentVal = Helper.findAndSetKeyInObject(
          _contextData,
          props.multipleParentId
        );
        field.value.value =
          parentVal?.[props.recordIndex]?.[field.value.id] || null;
      } else {
        // For regular fields, use the original logic
        // only override the value if ignoreOverridePageValue is false
        if (!props.ignoreOverridePageValue) {
          field.value.value = Helper.findAndSetKeyInObject(
            _contextData,
            field.value.id
          );
        }
      }
      // Convert ISO date string to Date object for DatePicker
      // Required because saved dates are stored as ISO strings but DatePicker expects Date objects
      if (
        field.value.fieldObj.field_type ===
          Constants.enums.FIELD_TYPES.INPUT_DATE_TIME_PICKER &&
        field.value.value &&
        typeof field.value.value === "string"
      ) {
        field.value.value = new Date(field.value.value);
      }

      // Track previous visibility state for clearOnHidden functionality
      const previousVisibility = ref(props.isVisible);

      const cameraInput = ref(null);
      const newImageObj = ref({
        isVisible: false,
        url: "",
        name: "",
        type: "image/jpeg"
      });
      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });
      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });
      const docObj = ref({
        isVisible: false,
        url: "",
        fileName: "",
        media_type: ""
      });
      const confirmDialogObj = ref({
        isVisible: false,
        headerMsg: "",
        message: "",
        showHazardIcon: false,
        onConfirm: () => {}
      });
      const addMoreMenuObj = ref({
        isVisible: false,
        title: "",
        pageObj: {},
        onSaveClick: () => {}
      });
      const googleMapsDialog = ref({
        isVisible: false,
        location: null,
        draggable: false
      });
      const informationDialog = ref({
        isVisible: false,
        header: "",
        message: "",
        showIcon: true
      });
      const mediaSourceDialog = ref({
        isVisible: false
      });
      const signatureObj = ref({
        isVisible: false,
        onConfirm: (val) => onSignatureConfirm(val)
      });
      const onSignatureConfirm = (val) => {
        field.value.value = val;
        updateStore(val);
      };

      // Get attendee data from response object
      const attendeeSignaturesData = computed(() => {
        if (!field.value.attendeeDataKey) return [];

        // Use findAndSetKeyInObject for simple key lookups
        const attendeeData = Helper.findAndSetKeyInObject(
          response.value,
          field.value.attendeeDataKey
        );

        if (Array.isArray(attendeeData)) {
          return attendeeData;
        }

        return [];
      });

      const htmlIsEditable = computed(() => {
        if (!Helper.isValueExist(field.value.is_editable)) {
          return false;
        }

        // Avoid deep copy if simple boolean
        if (typeof field.value.is_editable === "boolean") {
          return field.value.is_editable;
        }

        // Only deep copy response if we need dynamic evaluation
        const dynamicValue = Helper.findDynamicKeyInExpression(
          field.value.is_editable,
          response.value
        );
        return dynamicValue === "true";
      });

      const htmlIsRemovable = computed(() => {
        if (!Helper.isValueExist(field.value.is_removable)) {
          return false;
        }

        // Avoid deep copy if simple boolean
        if (typeof field.value.is_removable === "boolean") {
          return field.value.is_removable;
        }

        // Only deep copy response if we need dynamic evaluation
        const dynamicValue = Helper.findDynamicKeyInExpression(
          field.value.is_removable,
          response.value
        );
        return dynamicValue === "true";
      });

      const formTemplateData = computed(() => {
        const formTemplateStore = useFormTemplateStore();
        return formTemplateStore.templateList.find(
          (template) => template.id === templateId.value
        );
      });

      const onBtnClick = async (type) => {
        try {
          switch (type) {
            case Constants.enums.NAVIGATION_BTN_ACTIONS.GET_AND_SET_LATLONG:
              const location = await Helper.getCurrentLocationLatLong();
              Helper.findAndSetKeyInObject(
                response.value,
                field.value.id,
                location
              );
              if (
                field.value?.update_fields &&
                Array.isArray(field.value.update_fields)
              ) {
                field.value.update_fields.forEach((item) => {
                  if (
                    item &&
                    typeof item === "object" &&
                    item.value &&
                    item.key
                  ) {
                    let _value = Helper.findDynamicKeyInExpression(
                      `${item.value}`,
                      Helper.deepCopy(response.value)
                    );
                    if (_value !== undefined) {
                      Helper.findAndSetKeyInObject(
                        response.value,
                        item.key,
                        _value
                      );
                    }
                  }
                });
              }
              break;

            case Constants.enums.NAVIGATION_BTN_ACTIONS.OPEN_GOOGLE_MAPS_DIALOG:
              // Check if there's already a saved location
              const existingLocation = Helper.findAndSetKeyInObject(
                response.value,
                field.value.id
              );

              if (
                existingLocation &&
                existingLocation.latitude &&
                existingLocation.longitude
              ) {
                googleMapsDialog.value.location = existingLocation;
              } else {
                const currentLocation =
                  await Helper.getCurrentLocationLatLong();
                googleMapsDialog.value.location = currentLocation;
              }

              googleMapsDialog.value.clickable =
                field.value.pin_moveable || false;
              googleMapsDialog.value.isVisible = true;
              break;

            case Constants.enums.NAVIGATION_BTN_ACTIONS.REVERSE_START_END:
              const fieldId = field.value?.parent_field_id;
              const swapFields = field.value?.swap_fields;

              if (!fieldId || !Array.isArray(swapFields)) return;

              const current = Helper.findAndSetKeyInObject(
                response.value,
                fieldId
              );
              if (!current) return;

              const swapped = { ...current };

              for (const [fieldA, fieldB] of swapFields) {
                const temp = swapped[fieldA];
                swapped[fieldA] = swapped[fieldB];
                swapped[fieldB] = temp;
              }

              Helper.findAndSetKeyInObject(response.value, fieldId, swapped);
              break;

            case Constants.enums.NAVIGATION_BTN_ACTIONS.OPEN_POTHOLE_FORM:
              addMoreMenuObj.value.pageObj = await fetchFormTemplateById();
              addMoreMenuObj.value.isVisible = true;
              addMoreMenuObj.value.title = field.value.label || "Open Form";
              addMoreMenuObj.value.onSaveClick = onAddMoreMenuOptionSave;
              break;
            // moving to next page
            case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_IMG_INFO_TRIGGER_BTN:
              docObj.value.isVisible = true;
              docObj.value.url = field.value.url;
              docObj.value.fileName = field.value.label;
              docObj.value.media_type = field.value.media_type;
              break;
            case Constants.enums.NAVIGATION_BTN_ACTIONS
              .ON_CAMERA_SELECT_TRIGGER_BTN:
              cameraInput.value.triggerFileInput();
              break;
            case Constants.enums.NAVIGATION_BTN_ACTIONS
              .ON_FILE_SELECT_TRIGGER_BTN:
              console.log("ON_FILE_SELECT_TRIGGER_BTN >>> ");
              // On mobile/tablet: show dialog to choose between camera or gallery
              // On desktop: directly open file picker
              console.log("Helper.isMobileBrowser() >>> ", Helper.isMobileBrowser());
              if (Helper.isMobileBrowser()) {
                mediaSourceDialog.value.isVisible = true;
              } else {
                cameraInput.value.triggerGallery();
              }
              break;
            case Constants.enums.NAVIGATION_BTN_ACTIONS.ON_ROUTE_TO_PAGE: { // Get dynamic route value if applicable
              const routePath = field.value?.route_to
                ? Helper.findDynamicKeyInExpression(
                    field.value.route_to,
                    Helper.deepCopy(response.value)
                  )
                : null;
              if (routePath)
                Helper.handleRouteNavigation(router, route, routePath);
              break;
            }

            case Constants.enums.NAVIGATION_BTN_ACTIONS.SHOW_INFORMATION_DIALOG:
              informationDialog.value.header =
                field.value.dialog_title || "Information";
              informationDialog.value.message =
                field.value.dialog_content || "";
              informationDialog.value.showIcon =
                field.value.show_icon !== false;
              informationDialog.value.isVisible = true;
              break;

            default:
              break;
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };
      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };
      // file / Image selection from file input or camera
      // Add file queue state
      const fileQueue = ref([]);
      const isProcessingQueue = ref(false);

      // Process the next file in queue
      const processNextFile = async () => {
        if (fileQueue.value.length === 0) {
          isProcessingQueue.value = false;
          return;
        }

        const nextFile = fileQueue.value[0];
        await prepareImageForEdit(nextFile);
      };

      // Handler for file selection from file input or camera
      const handleFileSelection = async (event) => {
        // show loader
        loaderObj.value.isLoading = true;
        loaderObj.value.loaderMsg = "Processing Documents...";
        let files = Array.from(event.target.files);
        if (files.length === 0) return;

        // sorty all the files by its types and push images at the end
        files = files.sort((a, b) => {
          if (Helper.isImage(a) && !Helper.isImage(b)) return 1;
          if (!Helper.isImage(a) && Helper.isImage(b)) return -1;
          return 0;
        });

        for (const file of files) {
          // add to queue if it's image
          if (Helper.isImage(file)) {
            // Convert HEIC/HEIF to JPEG so the dialog can show the image (JPEG keeps file size reasonable vs PNG)
            const displayableFile = Helper.isNonDisplayableImageFile(file)
              ? await Helper.convertToDisplayableImageFile(file, {
                  outputType: "image/jpeg",
                  quality: 0.92
                })
              : file;
            fileQueue.value.push(displayableFile);
          } else {
            try {
              loaderObj.value.isLoading = true;
              loaderObj.value.loaderMsg = "Uploading Document...";
              const result =
                await useSupabaseStorageUploadStore().uploadFormAttachment(
                  file
                );
              if (!field.value.value) field.value.value = [];
              field.value.value.push(result);
              updateStore(field.value.value);
            } catch (error) {
              errorObj.value.isVisible = true;
              errorObj.value.errorMsg = error;
            } finally {
              loaderObj.value.isLoading = false;
            }
          }
        }
        // hide loader
        loaderObj.value.isLoading = false;
        loaderObj.value.loaderMsg = "";

        // Start processing if not already processing
        if (!isProcessingQueue.value) {
          isProcessingQueue.value = true;
          processNextFile();
        }
      };
      // Prepare an image for editing
      const prepareImageForEdit = async (file) => {
        let imageToEdit = file;
        let imageUrl = URL.createObjectURL(file);

        // Reset object first to ensure reactivity
        newImageObj.value = {
          isVisible: false,
          url: "",
          name: "",
          type: "image/jpeg",
          originalFile: null
        };

        // Use setTimeout to ensure proper DOM update cycle
        setTimeout(() => {
          newImageObj.value = {
            isVisible: true,
            url: imageUrl,
            name: file.name,
            type: file.type,
            originalFile: imageToEdit
          };
        }, 0);
      };

      // Handle edit dialog visibility changes
      const handleImageEditDialogVisibility = (isVisible) => {
        newImageObj.value.isVisible = isVisible;

        // Clean up when dialog is closed
        if (!isVisible) {
          // Remove the processed file from queue
          fileQueue.value.shift();

          // Reset the object to prevent reusing revoked URLs
          newImageObj.value = {
            isVisible: false,
            url: "",
            name: "",
            type: "image/jpeg",
            originalFile: null
          };

          // Process next file in queue
          processNextFile();
        }
      };

      const processFiles = async (files) => {
        try {
          const file = Array.isArray(files) ? files[0] : files;

          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              loaderObj.value.isLoading = true;
              loaderObj.value.loaderMsg = "Uploading Document...";
              // Create base64 data
              let base64Data = Helper.createBase64FromBuffer(
                file.type,
                e.target.result
              );
              base64Data = await Helper.getBase64FromBlob(file);

              // Generate IDs and paths
              const _attachmentId = Helper.getRandomUuid();
              const _attachmentMediaType =
                Helper.getMediaTypeFromBase64(base64Data);
              const _attachmentExtension =
                Helper.getExtensionFromBase64(base64Data);
              const _attachmentFileName = `${_attachmentId}.${_attachmentExtension}`;
              const _attachmentRemoteUrl = `${Helper.getRemoteStoragePrefix()}${Helper.getUserFlraAttachmentsBucket()}/${_attachmentFileName}`;
              const _attachmentLocalUrl = Helper.base64ToBlobUrl(
                base64Data,
                _attachmentMediaType
              );

              const attachmentsStore = useAttachmentsStore();
              await attachmentsStore.insertAttachment({
                id: _attachmentId,
                url: _attachmentRemoteUrl,
                base64: base64Data,
                by: Helper.getCurrentUserId()
              });
              // Add to attachments list
              const _record = {
                id: _attachmentId,
                filename: _attachmentFileName,
                local_uri: _attachmentLocalUrl,
                url: _attachmentRemoteUrl,
                media_type: _attachmentMediaType
              };
              if (!field.value.value) field.value.value = [];
              if (_record) {
                field.value.value.push(_record);
                updateStore(field.value.value);
              }
            } catch (error) {
              errorObj.value.isVisible = true;
              errorObj.value.errorMsg = error;
            } finally {
              loaderObj.value.isLoading = false;
            }
          };

          reader.readAsArrayBuffer(file);
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      onMounted(async () => {
        await checkForPreDefinedData();
      });

      async function fetchFormTemplateById() {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Fetching Form...";
          let _res = await useFormTemplateStore().getFormTemplateById(
            field.value.add_options_form_template,
            true
          );
          if (_res.error) throw _res.error;
          // reset the page from list
          // Setting up the template data with no value
          for (const element of _res.data.pages) {
            if (element.page_id) {
              // if not list and have page_id then pull the pageObj from the server
              const _pageRes = await useFormPageStore().getFormPageById(
                element.page_id,
                true
              );
              if (_pageRes.error) throw _pageRes.error;
              element.pageObj = _pageRes.data;
            }
          }
          // returning data to the caller
          return _res.data.pages && _res.data.pages.length > 0
            ? _res.data.pages[0]
            : null;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      }

      const onAddNewMenuOption = async () => {
        try {
          addMoreMenuObj.value.pageObj = await fetchFormTemplateById();
          addMoreMenuObj.value.isVisible = true;
          addMoreMenuObj.value.title = field.value.label
            ? `Add New ${field.value.label}`
            : "Add New";
          addMoreMenuObj.value.onSaveClick = onAddMoreMenuOptionSave;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      const onAddMoreMenuOptionSave = async () => {
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Adding New Option...";
          // Generate template response from page object
          const templateResponse = Helper.generateFormTemplateResponse({
            id: null,
            pages: [addMoreMenuObj.value.pageObj]
          });

          // Deep copy to avoid mutations
          const fieldValues = Helper.deepCopy(templateResponse);

          // Find and set key in object
          let record = Helper.findAndSetKeyInObject(
            fieldValues,
            addMoreMenuObj.value.pageObj.id
          );

          // Add unique ID to record
          record = {
            ...record,
            id: Helper.getRandomUuid()
          };

          if (
            field.value.fieldObj.field_type ===
              Constants.enums.FIELD_TYPES.DROPDOWN &&
            field.value.options_ref
          ) {
            // Update predefined list with new record
            await usePreDefinedListStore().newPreDefinedListItem(
              field.value.options_ref,
              record
            );
            await checkForPreDefinedData();
          } else {
            field.value.value = record;
            updateStore(record);
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
          Helper.showToast("Successful", "Option added successfully");
        }
      };

      const onMenuOptionEdit = async (option, isHtmlEdit = false) => {
        try {
          addMoreMenuObj.value.pageObj = await fetchFormTemplateById();
          // merging the option data with the pageObj fields
          addMoreMenuObj.value.pageObj.pageObj.fields =
            Helper.processFieldValues(
              Helper.deepCopy(addMoreMenuObj.value.pageObj.pageObj.fields),
              option
            );
          addMoreMenuObj.value.isVisible = true;
          addMoreMenuObj.value.title = option.label
            ? `Update ${option.label}`
            : "Update Option";
          addMoreMenuObj.value.onSaveClick = async () => {
            try {
              loaderObj.value.isLoading = true;
              loaderObj.value.loaderMsg = "Updating Option...";
              // Generate template response from page object
              const templateResponse = Helper.generateFormTemplateResponse({
                id: null,
                pages: [addMoreMenuObj.value.pageObj]
              });

              // Deep copy to avoid mutations
              const fieldValues = Helper.deepCopy(templateResponse);

              // Find and set key in object
              let record = Helper.findAndSetKeyInObject(
                fieldValues,
                addMoreMenuObj.value.pageObj.id
              );

              if (isHtmlEdit) {
                // Store the override in the form response
                if (!response.value[templateId.value]._listOverrides) {
                  response.value[templateId.value]._listOverrides = {};
                }
                if (
                  !response.value[templateId.value]._listOverrides[
                    field.value.options_ref
                  ]
                ) {
                  response.value[templateId.value]._listOverrides[
                    field.value.options_ref
                  ] = {};
                }

                response.value[templateId.value]._listOverrides[
                  field.value.options_ref
                ][option.id] = record;

                // Update the store
                formTemplateResponseStore.setResponse(
                  templateId.value,
                  response.value[templateId.value]
                );

                // Make a copy of field.value.value
                const currentValue = Helper.deepCopy(
                  // response.value[templateId.value][field.value.parent_field_id]
                  Helper.findAndSetKeyInObject(
                    response.value,
                    `${field.value.parent_field_id}`
                  )
                );

                // Merge record into the copy, overriding any fields with same name
                const mergedValue = { ...currentValue, ...record };

                // Update store with merged value
                updateStore(mergedValue, field.value.parent_field_id);
              } else {
                await usePreDefinedListStore().updatePreDefinedListItem(
                  field.value.options_ref,
                  {
                    ...record,
                    id: option.id
                  }
                );
                // if the option is the same as the current value, then update the store
                if (field.value.value && field.value.value.id === option.id) {
                  field.value.value = {
                    ...record,
                    id: option.id
                  };
                  updateStore(field.value.value);
                }
              }
              // if we've ref of the option, then we need to check for pre-defined data
              if (field.value.options_ref) {
                await checkForPreDefinedData();
              }
            } catch (error) {
              errorObj.value.isVisible = true;
              errorObj.value.errorMsg = error;
            } finally {
              loaderObj.value.isLoading = false;
              Helper.showToast("Successful", "Option updated successfully");
            }
          };
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      const onMenuOptionDelete = (option) => {
        confirmDialogObj.value.isVisible = true;
        confirmDialogObj.value.headerMsg = `Delete ${option?.label}`;
        confirmDialogObj.value.message = `Are you sure you want to <b class='text-red-600'>delete</b> <b>${option?.label}</b> ?`;
        confirmDialogObj.value.onConfirm = async () => {
          try {
            loaderObj.value.isLoading = true;
            loaderObj.value.loaderMsg = "Deleting Option...";
            // Update predefined list with new record
            await usePreDefinedListStore().deletePreDefinedListItem(
              field.value.options_ref,
              option.id
            );
            await checkForPreDefinedData();
          } catch (error) {
            errorObj.value.isVisible = true;
            errorObj.value.errorMsg = error;
          } finally {
            loaderObj.value.isLoading = false;
            Helper.showToast("Successful", "Option deleted successfully");
          }
        };
      };

      // gets the options for the select dropdown
      const getMenuOptions = () => {
        try {
          // Get base list items with overrides applied
          let listItems = getBaseListWithOverrides();

          // Apply containMatch filtering if enabled
          if (field.value.showContainMatch) {
            const dynamicKey = getDynamicKeyForContainMatch();
            const isExclude = field.value.containMatchType === "exclude";

            // Return empty array if no dynamic key found
            if (!dynamicKey) {
              return [];
            }

            // Filter items that contain the dynamic key or not contain the dynamic key
            if (Array.isArray(listItems)) {
              listItems = listItems.filter((item) => {
                const contains = Object.values(item).includes(dynamicKey);
                return isExclude ? !contains : contains;
              });
            } else {
              listItems = [];
            }
          }

          return listItems;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
          return [];
        }
      };

      // Helper function to get base list with overrides
      const getBaseListWithOverrides = () => {
        const baseList = Helper.deepCopy(field.value.options || []);

        if (!Array.isArray(baseList)) {
          return [];
        }

        const overrides =
          response.value[templateId.value]?._listOverrides?.[
            field.value.options_ref
          ];

        if (!overrides) {
          return baseList;
        }

        return baseList.map((item) => ({
          ...item,
          ...overrides[item.id]
        }));
      };

      // Helper function to get dynamic key for contain match filtering
      const getDynamicKeyForContainMatch = () => {
        if (
          !field.value.showContainMatch ||
          typeof field.value.showContainMatch !== "string"
        ) {
          return null;
        }

        if (props.isMultiple || props.isSubPage) {
          // Get context for multiple/subpage fields
          const parentVal = Helper.findAndSetKeyInObject(
            Helper.deepCopy(response.value),
            props.multipleParentId
          );

          if (!parentVal?.[props.recordIndex]) {
            return null;
          }

          return Helper.findDynamicKeyInExpression(
            field.value.showContainMatch,
            parentVal[props.recordIndex]
          );
        } else {
          // Get context for regular fields
          return Helper.findDynamicKeyInExpression(
            field.value.showContainMatch,
            Helper.deepCopy(response.value)
          );
        }
      };

      // For select dropdown
      let originalListClone = ref([]);
      // checks for pre-defined list and pre-populates the options
      const checkForPreDefinedData = async () => {
        try {
          // if it's dropdown and has options_ref then pull list from the pre-defined list from DB table - 'pre_defined_lists',
          // it'll use store to get those data since we're pre-populating data from table
          if (
            (field.value.fieldObj.field_type ==
              Constants.enums.FIELD_TYPES.DROPDOWN ||
              field.value.fieldObj.field_type ==
                Constants.enums.FIELD_TYPES.SIMPLE_NAME_INPUT) &&
            helper.isValueExist(field.value.options_ref_config)
          ) {
            const permissionsStore = usePermissionsStore();
            let _res = null;
            if (
              field.value.options_ref_config.table_name ===
                Constants.enums.TABLES_NAME_ENUM.MASTER_COMPANIES_TABLE &&
              field.value.options_ref_config &&
              field.value.options_ref_config.permissions_level
            ) {
              _res = await permissionsStore.getCompanies(
                field.value.options_ref_config.config,
                field.value.options_ref_config.permissions_level
              );
            } else if (
              field.value.options_ref_config.table_name ===
                Constants.enums.TABLES_NAME_ENUM.MASTER_SHOPS_TABLE &&
              field.value.options_ref_config &&
              field.value.options_ref_config.permissions_level
            ) {
              _res = await permissionsStore.getShops(
                field.value.options_ref_config.config,
                field.value.options_ref_config.permissions_level
              );
            } else if (
              field.value.options_ref_config.table_name ===
                Constants.enums.TABLES_NAME_ENUM.MASTER_REGIONS_TABLE &&
              field.value.options_ref_config &&
              field.value.options_ref_config.permissions_level
            ) {
              _res = await permissionsStore.getRegions(
                field.value.options_ref_config.config,
                field.value.options_ref_config.permissions_level
              );
            } else {
              _res = await usePreDefinedListStore().getPreDefinedListData(
                field.value.options_ref_config.config,
                field.value.options_ref_config.table_name
              );
            }
            if (_res.error) {
              throw Error(
                `Error while fetching dynamic list data : ${_res.error}`
              );
            }
            if (_res.data) {
              // if additional fields need to get added to the list
              if (field.value.options_ref_config.additional_fields_to_include) {
                // Add additional fields to each item in the data array
                _res.data = _res.data.map((item) => {
                  const newItem = { ...item };
                  // Process each additional field
                  field.value.options_ref_config.additional_fields_to_include.forEach(
                    (additionalField) => {
                      if (Helper.isValueExist(additionalField.value)) {
                        newItem[additionalField.filed] =
                          // Use helper function to evaluate the value expression
                          Helper.findDynamicKeyInExpression(
                            additionalField.value,
                            newItem
                          );
                      } else {
                        // If value doesn't exist, set field to null
                        newItem[additionalField.filed] = null;
                      }
                    }
                  );
                  return newItem;
                });
              }
            }
            field.value.options = _res.data;
            originalListClone.value = field.value.options;
          } else if (
            field.value.fieldObj.field_type ==
              Constants.enums.FIELD_TYPES.DROPDOWN &&
            helper.isValueExist(field.value.options_ref)
          ) {
            field.value.options =
              await usePreDefinedListStore().getPreDefinedListById(
                field.value.options_ref
              );

            originalListClone.value = field.value.options;
          }

          // for auto increment, if the value is not set, then get the last value from the DB table, unless it has a manual-generation set to true
          if (
            field.value.fieldObj.field_type ==
              Constants.enums.FIELD_TYPES.AUTO_INCREMENT &&
            !Helper.isValueExist(field.value.value) &&
            !field.value.manual_generation
          ) {
            await generateAutoIncrementValue();
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
        // Populate default date value if enabled
        if (
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.INPUT_DATE_TIME_PICKER &&
          !field.value.value &&
          field.value.enable_default_value
        ) {
          const formatted = Helper.getDefaultDateValue(
            field.value.default_value_mode
          );
          field.value.value = formatted;
          await nextTick();
          updateStore(formatted);
        }

        if (
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.INPUT_TXT &&
          field.value.default_value_mode ===
            Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_NAME &&
          !field.value.value
        ) {
          const name = Helper.getUserName?.();
          if (name) {
            field.value.value = name;
            await nextTick();
            updateStore(name);
          }
        }
        if (
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.INPUT_TXT &&
          field.value.default_value_mode ===
            Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_EMAIL &&
          !field.value.value
        ) {
          const email = Helper.getUserEmail?.();
          if (email) {
            field.value.value = email;
            await nextTick();
            updateStore(email);
          }
        }

        if (
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.INPUT_TXT &&
          field.value.default_value_mode ===
            Constants.enums.FIXED_FETCH_OPTIONS_KEYS.LATLONG &&
          !field.value.value
        ) {
          try {
            const location = await Helper.getCurrentLocationLatLong();
            if (location && location.latitude && location.longitude) {
              const latlongValue = `${location.latitude}, ${location.longitude}`;
              field.value.value = latlongValue;
              await nextTick();
              updateStore(latlongValue);
            }
          } catch (error) {
            console.error("Error getting location for default value:", error);
          }
        }
      };

      // New method to generate autoincrement value that can be called from outside
      const generateAutoIncrementValue = async (shouldReturnVal = false) => {
        try {
          return await Helper.generateAutoIncrementValue({
            field: field.value,
            response: response.value,
            templateId: templateId.value,
            relevantResponseData: relevantResponseData.value,
            updateStore,
            shouldReturnVal
          });
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };
      // will be used to evaluate the formula
      // will return the value, it'll not set value to the field
      // const evaluateFormula = (formula) => {
      //   try {
      //     const decimalPlaces = field.value?.decimalPlaces ?? 2;

      //     const regex = /\$\(([^)]+)\)(?:\.(\w[\w-]*))?/g;
      //     let total = 0;

      //     const replacedFormula = formula.replace(
      //       regex,
      //       (match, fieldId, key) => {
      //         let val;
      //         if (props.isMultiple) {
      //           const parentVal = Helper.findAndSetKeyInObject(
      //             response.value,
      //             props.multipleParentId
      //           );
      //           val = parentVal?.[props.recordIndex]?.[fieldId] ?? 0;
      //         } else {
      //           val = Helper.findAndSetKeyInObject(response.value, fieldId);
      //         }

      //         if (key) {
      //           if (Array.isArray(val)) {
      //             total = val.reduce((sum, item) => {
      //               const keyVal = item[key];
      //               return (
      //                 sum +
      //                 (!isNaN(parseFloat(keyVal)) ? parseFloat(keyVal) : 0)
      //               );
      //             }, 0);
      //             val = total;
      //           } else if (
      //             typeof val === "object" &&
      //             val !== null &&
      //             key in val
      //           ) {
      //             val = val[key];
      //           } else {
      //             return "0";
      //           }
      //         }
      //         return !isNaN(val) ? val.toFixed(decimalPlaces) : "0.00";
      //       }
      //     );

      //     if (!replacedFormula) return 0.0;
      //     const result = eval(replacedFormula);
      //     return isNaN(result) ? 0.0 : result.toFixed(decimalPlaces);
      //   } catch (error) {
      //     errorObj.value.isVisible = true;
      //     errorObj.value.errorMsg = error;
      //     return 0.0;
      //   }
      // };
      const evaluateFormula = (formula) => {
        try {
          return Helper.evaluateFormula(formula, response.value, {
            isMultiple: props.isMultiple,
            multipleParentId: props.multipleParentId,
            recordIndex: props.recordIndex,
            decimalPlaces: field.value?.decimalPlaces ?? 2
          });
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
          return 0.0;
        }
      };

      // Add computed property to track only relevant response data
      const relevantResponseData = computed(() => {
        if (
          (props.isMultiple || props.isSubPage) &&
          Helper.isValueExist(props.multipleParentId)
        ) {
          const parentVal = Helper.findAndSetKeyInObject(
            response.value,
            props.multipleParentId
          );
          return parentVal?.[props.recordIndex]?.[field.value.id] || null;
        } else if (field.value?.id) {
          return Helper.findAndSetKeyInObject(_contextData, field.value.id);
        }
        return null;
      });

      // To track the number of times the calculated field is updated
      const updateCounts = ref(new Map());
      // Helper function to get field count considering multiple fields
      const getFieldCount = (fieldId) => {
        return updateCounts.value.get(fieldId) || 0;
      };

      // Track previous dependencies to avoid unnecessary recalculations
      const previousDependencies = ref({
        fieldValue: null,
        responseData: null,
        formula: null
      });

      const calculatedValue = computed(() => {
        // Get field ID, handling multiple fields case
        const fieldId =
          props.isMultiple &&
          props.multipleParentId &&
          Helper.isValueExist(props.recordIndex)
            ? `${props.multipleParentId}_${props.recordIndex}_${field.value?.id}`
            : field.value?.id;

        // Update counter if field ID exists
        if (fieldId) {
          updateCounts.value.set(fieldId, getFieldCount(fieldId) + 1);
        }

        // Handle different field types
        if (!field.value) {
          return "";
        }

        const fieldType = field.value.fieldObj?.field_type;
        const formula = field.value.formula;
        let result = "";

        // Calculate value based on field type
        if (fieldType === Constants.enums.FIELD_TYPES.CALCULATED) {
          result = evaluateFormula(formula || "");
        } else if (
          fieldType === Constants.enums.FIELD_TYPES.REF_FIELD &&
          Helper.isValueExist(formula)
        ) {
          result = Helper.findDynamicKeyInExpression(
            formula,
            Helper.deepCopy(response.value)
          );
        } else if (Helper.isValueExist(formula)) {
          const value = Helper.findDynamicKeyInExpression(
            formula,
            Helper.deepCopy(response.value)
          );

          // Only update value if field has been updated multiple times
          if (getFieldCount(fieldId) > 1) {
            result = value;
          } else {
            // For first run, use the current field value
            result = field.value.value || "";
          }
        } else {
          // For non-formula fields, use the current field value
          result = field.value.value || "";
        }

        // Remove the circular dependency - don't update field.value.value here
        // The watcher will handle updating the field value
        return result || "";
      });

      // updates the store data
      const updateStore = (newVal, fieldId = null) => {
        try {
          console.log("UPDATING STORE :", newVal, fieldId, field.value.id);

          // check if the updateLocalStore is false, then don't update the store
          if (!props.updateLocalStore) return;
          if (
            props.isMultiple &&
            Helper.isStringValueExist(props.multipleParentId) &&
            Helper.isValueExist(props.recordIndex)
          ) {
            const parentVal =
              Helper.findAndSetKeyInObject(
                Helper.deepCopy(response.value || {}),
                props.multipleParentId
              ) || [];

            // if the recordIndex doesn't exist, then create it
            if (!parentVal[props.recordIndex]) {
              parentVal[props.recordIndex] = {};
            }

            // update the value
            parentVal[props.recordIndex][fieldId || field.value.id] = newVal;

            // if the selectedPath is exist then use it, otherwise use the multipleParentId and recordIndex
            if (props.selectedPath && props.selectedPath.length > 0) {
              const pathString = [
                ...props.selectedPath,
                props.multipleParentId,
                props.recordIndex,
                fieldId || field.value.id
              ].join(".");
              const updatedResponse = Helper.setValueByPath(
                response.value,
                pathString,
                newVal
              );
              console.log("UPDATED RESPONSE :", updatedResponse);
              formTemplateResponseStore.setResponse(updatedResponse);
            } else {
              formTemplateResponseStore.setResponse(
                props.multipleParentId,
                parentVal
              );
            }
          } else if (props.isSubPage) {
            const targetRowId = props.multipleParentId.rowId;
            const parentRecordIndex = props.multipleParentId.recordIndex;

            // find the target row
            const parentVal = Helper.findAndSetKeyInObject(
              Helper.deepCopy(response.value),
              targetRowId
            );

            // check if the parentVal is an array and the parentRecordIndex exists
            if (
              parentVal &&
              Array.isArray(parentVal) &&
              parentVal[parentRecordIndex] !== undefined
            ) {
              const targetObject = parentVal[parentRecordIndex];

              // if the props.recordIndex doesn't exist, then create it
              if (
                !targetObject["sub_page"] ||
                !targetObject["sub_page"][props.recordIndex]
              ) {
                if (!targetObject["sub_page"]) {
                  targetObject["sub_page"] = {};
                }
                targetObject["sub_page"][props.recordIndex] = {};
              }
              targetObject["sub_page"][props.recordIndex][
                fieldId || field.value.id
              ] = newVal;
              targetObject["sub_page"][props.recordIndex][`index`] =
                props.recordIndex;
              if (
                Helper.isValueExist(field.value?.save_form_meta_data_key) &&
                Helper.isValueExist(props.rowGroup?.meta_data)
              ) {
                const metaDataKeys = field.value.save_form_meta_data_key || [];
                for (let i = 0; i < metaDataKeys.length; i++) {
                  const element = metaDataKeys[i];
                  if (
                    element &&
                    props.rowGroup.meta_data[element] !== undefined
                  ) {
                    targetObject["sub_page"][props.recordIndex][element] =
                      props.rowGroup?.meta_data?.[element] || null;
                  }
                }
              }
              formTemplateResponseStore.setResponse(targetRowId, parentVal);
            }
          } else if (props.selectedPath && props.selectedPath.length > 0) {
            const pathString = [
              ...props.selectedPath,
              fieldId || field.value.id
            ].join(".");
            const updatedResponse = Helper.setValueByPath(
              Helper.deepCopy(response.value),
              pathString,
              newVal
            );
            // we are resetting the response object with the updated value
            formTemplateResponseStore.setResponse(updatedResponse);
            return;
          } else {
            const updatedVal = Helper.findAndSetKeyInObject(
              Helper.deepCopy(response.value),
              fieldId || field.value.id,
              newVal
            );
            formTemplateResponseStore.setResponse(
              templateId.value,
              updatedVal[templateId.value]
            );
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };
      const updateTimer = ref(null);
      // gets used to update the store after 100ms of inactivity to prevent recursive calls
      const debouncedUpdateStore = (newVal) => {
        try {
          if (updateTimer.value) {
            clearTimeout(updateTimer.value);
          }
          updateTimer.value = setTimeout(() => updateStore(newVal), 100);
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      // Cleanup in onUnmounted
      onUnmounted(() => {
        if (updateTimer.value) {
          clearTimeout(updateTimer.value);
        }
      });

      // Watch for visibility changes and clear field value if clearOnHidden is true
      watch(
        () => props.isVisible,
        (newVisibility, oldVisibility) => {
          // If field becomes visible and it was previously hidden and clearOnHidden is true
          if (newVisibility && !oldVisibility && props.clearOnHidden) {
            // Clear the field value by setting it to null
            updateStore(null, field.value.id);
            // Also clear the local field value
            if (field.value) {
              field.value.value = null;
            }
          }
          // Update the previous visibility state
          previousVisibility.value = newVisibility;
        }
      );

      // on new value of calculated field, update the field value and store the value
      watch(calculatedValue, (newVal) => {
        try {
          if (
            field.value?.fieldObj?.field_type ===
            Constants.enums.FIELD_TYPES.CALCULATED
          ) {
            field.value.value = newVal;
            // Only update store if the value has actually changed
            if (relevantResponseData.value !== newVal) {
              debouncedUpdateStore(newVal);
            }
          } else if (field.value && field.value.formula) {
            let value = Helper.findDynamicKeyInExpression(
              field.value.formula || "",
              Helper.deepCopy(response.value)
            );
            field.value.value = value;
            if (relevantResponseData.value !== value) {
              debouncedUpdateStore(value);
            }
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      });

      const _onUpdateField = async (val, fieldId = null) => {
        try {
          const fieldType = field.value.fieldObj.field_type;

          // Handle date-time picker fields
          if (
            fieldType === Constants.enums.FIELD_TYPES.INPUT_DATE_TIME_PICKER &&
            val instanceof Date
          ) {
            field.value.value = val;
          }

          // Handle on_change_remove for all field types
          if (field.value.on_change_remove) {
            field.value.on_change_remove.forEach((elementId) => {
              updateStore(null, elementId);
            });
          }

          // Trigger blade max validation when Shop or Blade Type changes
          if (field.value.trigger_blade_max_validation) {
            const config = field.value.trigger_blade_max_validation;
            const otherFieldId = config.other_field_id;

            // current field value
            const currentFieldValue = val;

            // other field value
            const otherField = Helper.findAndSetKeyInObject(
              response.value,
              otherFieldId
            );

            const shopId =
              config.type === Constants.enums.BLADE_INVENTORY.TYPE.SHOP
                ? currentFieldValue?.id
                : otherField?.id;

            const bladeTypeId =
              config.type === Constants.enums.BLADE_INVENTORY.TYPE.BLADE_TYPE
                ? currentFieldValue?.id
                : otherField?.id;

            if (shopId && bladeTypeId) {
              const maxQty = await Helper.getMaxBladeTransferQuantity(
                shopId,
                bladeTypeId
              );

              // Qty field ID
              const qtyFieldId = config.qty_field_id;
              if (
                props.isMultiple &&
                props.multipleParentId &&
                Helper.isValueExist(props.recordIndex)
              ) {
                const parentVal = Helper.findAndSetKeyInObject(
                  response.value,
                  props.multipleParentId
                );
                if (parentVal?.[props.recordIndex]) {
                  parentVal[props.recordIndex][`${qtyFieldId}_max`] = maxQty;
                }
              }
            }
          }

          console.log(
            "RELEVANT RESPONSE DATA :",
            relevantResponseData.value,
            val
          );
          // Update store if value changed
          if (relevantResponseData.value !== val) {
            updateStore(val);
          }

          // Update additional field if specified
          if (fieldId) {
            console.log(`UPDATING ${fieldId} to ${val}`);
            updateStore(val, fieldId);
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };
      // if it's INPUT or TXT_AREA then debounce for 500ms, otherwise debounce for 0ms
      const onUpdateField = _.debounce(
        _onUpdateField,
        field.value.fieldObj.field_type ===
          Constants.enums.FIELD_TYPES.INPUT_TXT ||
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.TXT_AREA
          ? Constants.enums.DEBOUNCE_TIME.MS_500
          : Constants.enums.DEBOUNCE_TIME.MS_0
      );

      const getHiddenFieldContent = () => {
        let content = Helper.deepCopy(field.value.value);
        console.log("CONTENT :", content);
        content = content
          ? Helper.findDynamicKeyInExpression(
              content,
              Helper.deepCopy(response.value)
            )
          : "";
        // if defined is_static == true then don't update the field value
        if (
          relevantResponseData.value !== content &&
          (!Helper.isValueExist(field.value.is_static) ||
            field.value.is_static !== true)
        ) {
          // update the field value to reflect the change in the UI
          field.value.value = content;
          // update the store
          updateStore(content);
        }
      };

      const onHtmlEdit = (fieldInput) => {
        let option;
        if (
          props.isMultiple &&
          props.multipleParentId &&
          props.recordIndex >= 0
        ) {
          const parentVal = Helper.findAndSetKeyInObject(
            response.value,
            props.multipleParentId
          );
          option = parentVal[props.recordIndex];
        } else {
          option = Helper.findDynamicKeyInExpression(
            `$(${fieldInput.parent_field_id})`,
            Helper.deepCopy(response.value)
          );
        }

        onMenuOptionEdit(option, true);
      };

      const onHtmlRemove = () => {
        confirmDialogObj.value.isVisible = true;
        confirmDialogObj.value.headerMsg = `Remove Data`;
        confirmDialogObj.value.message = `Are you sure you want to remove ?`;
        confirmDialogObj.value.showHazardIcon = false;
        confirmDialogObj.value.onConfirm = () => {
          const parentFieldId = field.value.parent_field_id;
          if (parentFieldId) {
            if (
              props.isMultiple &&
              props.multipleParentId &&
              props.recordIndex >= 0
            ) {
              const parentVal = Helper.findAndSetKeyInObject(
                response.value,
                props.multipleParentId
              );
              if (parentVal[props.recordIndex]) {
                parentVal[props.recordIndex][parentFieldId] = null;
                formTemplateResponseStore.setResponse(
                  props.multipleParentId,
                  parentVal
                );
              }
            } else {
              if (props.updateLocalStore) {
                // For non-multiple case, explicitly set the field to null
                const updatedVal = Helper.findAndSetKeyInObject(
                  Helper.deepCopy(response.value),
                  parentFieldId,
                  null
                );
                formTemplateResponseStore.setResponse(
                  templateId.value,
                  updatedVal[templateId.value]
                );
              }
            }
          }
          Helper.showToast("Successful", "Data removed successfully");
        };
      };

      // Add deep watcher for form template response store
      watch(
        () => response.value[templateId.value]?._listOverrides,
        async (newVal) => {
          // Only proceed if this is a dropdown field
          if (
            field.value?.fieldObj?.field_type ===
            Constants.enums.FIELD_TYPES.DROPDOWN
          ) {
            // Check if we have local overrides and the field ID exists in them
            if (newVal?.[field.value.options_ref] && field.value.id) {
              const localOverrides = newVal[field.value.options_ref];

              // If we have options, merge the overrides
              if (Array.isArray(field.value.options)) {
                field.value.options = field.value.options.map((option) => ({
                  ...option,
                  ...localOverrides[option.id]
                }));
              }

              // Set additional fields from local overrides if they exist
              const selectedOption = field.value.options?.find(
                (opt) => opt.id === field.value.value.id
              );
              if (selectedOption) {
                field.value.value = selectedOption || null;
              }
            }
          }
        },
        { deep: true }
      );

      // Compute the dynamic default value
      const computedDefaultValue = computed(() => {
        if (
          field.value?.defaultValue &&
          field.value.fieldObj.field_type ===
            Constants.enums.FIELD_TYPES.DROPDOWN
        ) {
          let context = Helper.deepCopy(response.value);
          if (
            props.isMultiple &&
            props.multipleParentId &&
            props.recordIndex >= 0
          ) {
            context = Helper.findAndSetKeyInObject(
              context,
              props.multipleParentId
            )[props.recordIndex];
          }

          return Helper.findDynamicKeyInExpression(
            field.value.defaultValue,
            context
          );
        }
        return null;
      });

      // Watch the computed default value
      watch(
        computedDefaultValue,
        (newDefaultVal) => {
          if (field.value.value !== newDefaultVal) {
            field.value.value = newDefaultVal;
            updateStore(newDefaultVal);
          }
        },
        { immediate: false }
      );

      // Watch for changes in the response store that affect this field
      watch(
        relevantResponseData,
        (newVal, oldVal) => {
          // Update the field value if it's different from the store value
          // This ensures that when update_fields updates values in the store,
          // the input components reflect those changes
          if (field.value.value !== newVal) {
            field.value.value = newVal;
          }
        },
        { deep: true }
      );

      const validateField = () => {
        if (
          Constants.enums.FIELD_TYPES_DONT_VALIDATE.includes(
            field.value.fieldObj.field_type
          )
        ) {
          return { isValid: true, message: "" };
        }

        // Skip validation if field is disabled
        if (field.value.disabled) {
          return {
            isValid: true,
            message: ""
          };
        }

        // Qty input validation
        if (field.value.enable_max_quantity_validation) {
          const val = field.value.value;
          const parentVal = Helper.findAndSetKeyInObject(
            response.value,
            props.multipleParentId
          );

          const maxQty =
            parentVal?.[props.recordIndex]?.[`${field.value.id}_max`];

          if (maxQty !== undefined && val > maxQty) {
            errorObj.value.isVisible = true;
            errorObj.value.errorMsg = `Transfer quantity ${val} cannot exceed. Available max quantity is ${maxQty}`;
            field.value.value = null;
            return {
              isValid: false,
              message: `Transfer quantity ${val} cannot exceed. Available max quantity is ${maxQty}`
            };
          } else {
          }
        }

        // Check if field is required
        if (
          field.value.required &&
          (!Helper.isValueExist(field.value.value) ||
            field.value.value.toString().trim() === "")
        ) {
          return {
            isValid: false,
            message: "This field is required"
          };
        }

        // Check regex if value exists and regex is specified
        if (field.value.value && field.value.required_regex) {
          try {
            // Remove the forward slashes from the regex string if present
            const regexPattern = field.value.required_regex.replace(
              /^\/|\/$/g,
              ""
            );
            const regex = new RegExp(regexPattern);

            if (!regex.test(field.value.value)) {
              console.error(
                `Field ${field.value.id} failed regex validation: ${field.value.required_regex}`
              );
              return {
                isValid: false,
                message: "Invalid format"
              };
            }
          } catch (error) {
            console.error("Invalid regex pattern:", error);
            return {
              isValid: true, // Fail open if regex is invalid
              message: ""
            };
          }
        }

        return {
          isValid: true,
          message: ""
        };
      };

      const applyValidationState = (preValidation = null) => {
        const validation =
          preValidation !== null ? preValidation : validateField();
        field.value.invalid = !validation.isValid;
        field.value.errorMessage = validation.message;
        return validation.isValid;
      };

      const processHtmlContent = (html) => {
        if (!html) return "";

        // Pass multiple field information to the unified function
        const multipleFieldInfo =
          props.isMultiple && props.multipleParentId && props.recordIndex >= 0
            ? {
                isMultiple: props.isMultiple,
                multipleParentId: props.multipleParentId,
                recordIndex: props.recordIndex
              }
            : null;

        // Delegate all template processing to the unified findDynamicKeyInExpression function
        // which now handles both syntaxes and multiple field contexts internally
        return Helper.findDynamicKeyInExpression(
          html,
          response.value,
          null,
          multipleFieldInfo
        );
      };

      const onAutoCompleteClick = async () => {
        if (autoCompleteSuggestions.value.length === 0) {
          autoCompleteSuggestions.value = getMenuOptions();
        }
        autoCompleteRef.value?.show();
      };

      const onAutoCompleteSearch = (event) => {
        const query = event.query.toLowerCase();
        const allOptions = getMenuOptions();

        if (!query) {
          autoCompleteSuggestions.value = allOptions;
        } else {
          autoCompleteSuggestions.value = allOptions.filter((option) =>
            option.label.toLowerCase().includes(query)
          );
        }
      };

      const isCameraInputRequired = computed(() => {
        // Check if any field in the form requires camera functionality
        return (
          field.value?.fieldObj?.field_type ===
            Constants.enums.FIELD_TYPES.TRIGGER_BTN &&
          (field.value?.onClick ===
            Constants.enums.NAVIGATION_BTN_ACTIONS
              .ON_CAMERA_SELECT_TRIGGER_BTN ||
            field.value?.onClick ===
              Constants.enums.NAVIGATION_BTN_ACTIONS.ON_FILE_SELECT_TRIGGER_BTN)
        );
      });

      const handleCoordinatesUpdated = (coordinates) => {
        // Handle the coordinates-updated event from Google Maps dialog
        const location = {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        };

        // Set the location in the response object the same way as GET_AND_SET_LATLONG
        Helper.findAndSetKeyInObject(response.value, field.value.id, location);

        // Update any additional fields if specified
        field.value?.update_fields?.forEach((item) => {
          let _value = Helper.findDynamicKeyInExpression(
            `${item.value}`,
            Helper.deepCopy(response.value)
          );
          Helper.findAndSetKeyInObject(response.value, item.key, _value);
        });
      };

      return {
        response,
        Helper,
        cameraInput,
        newImageObj,
        field,
        Constants,
        docObj,
        errorObj,
        loaderObj,
        confirmDialogObj,
        googleMapsDialog,
        onErrorDialogClose,
        onBtnClick,
        handleFileSelection,
        processFiles,
        handleImageEditDialogVisibility,
        onUpdateField,
        signatureObj,
        attendeeSignaturesData,
        onAddNewMenuOption,
        onMenuOptionEdit,
        onMenuOptionDelete,
        getMenuOptions,
        calculatedValue,
        relevantResponseData,
        addMoreMenuObj,
        getHiddenFieldContent,
        onHtmlEdit,
        onHtmlRemove,
        htmlIsEditable,
        htmlIsRemovable,
        generateAutoIncrementValue,
        validateField,
        applyValidationState,
        processHtmlContent,
        handleCoordinatesUpdated,
        autoCompleteSuggestions,
        onAutoCompleteSearch,
        onAutoCompleteClick,
        autoCompleteRef,
        isFieldHidden,
        isCameraInputRequired,
        informationDialog,
        mediaSourceDialog,
        onMediaSourceSelect: (source) => {
          if (source === "camera") {
            cameraInput.value.triggerCamera();
          } else if (source === "gallery") {
            cameraInput.value.triggerGallery();
          }
        }
      };
    }
  });
</script>
